import { getProductTemplateBlotterColumn } from '@src/components/OTC/ProductTemplateTooltip';
import type { OTCDealData } from '@src/types/OTC';
import { getColumnTitle, getColumnTooltip } from '@src/utils/fieldDefinitionUtils';
import {
  formatOtcDealBlotterLocalDateTime,
  formatOtcDealBookingTimezoneTooltip,
} from '@src/utils/OTC/otcDealDisplayUtils';
import { formatHolidayCalendars, formatProductType, formatSettlementCycle } from '@src/utils/OTC/otcUtils';
import { repoBookingFieldDefinitions } from '@src/utils/OTC/repoBookingFieldDefinitions';
import { LoanQuoteTerminationTypeEnum, useDefaultColumns, type Column, type ColumnDef } from '@talos/kyoko';
import { useMemo } from 'react';
import { otcDealUserColumn } from '../otcDealUserColumn';

export type RepoColumnMapId = keyof ReturnType<typeof useRepoColumnDefs>;

function getVisibleColumns(): Array<RepoColumnMapId> {
  return [
    'submitTime',
    'productType',
    'tradeDate',
    'effectiveDate',
    'status',
    'productTemplate',
    'symbol',
    'borrowedAsset',
    'repoAsset',
    'term',
    'termType',
    'repurchaseDate',
    'settlementDate',
    'counterparty',
    'marketAccount',
    'direction',
    'repoAmount',
    'rate',
    'primeBroker',
    'haircutPct',
    'purchasePrice',
    'repoInterest',
    'repurchasePrice',
    'subAccount',
    'user',
    'dealId',
    'businessDayConvention',
    'holidayCalendars',
    'timestamp',
  ];
}

export const useRepoColumns = (): Column[] => {
  const mapping = useRepoColumnDefs();

  const columnDefinitions = useMemo(() => {
    return new Map<string, Column>(
      Object.entries(mapping)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, { ...value, id: key }])
    );
  }, [mapping]);

  const visibleColumns = useMemo(() => {
    return getVisibleColumns().filter(item => !!columnDefinitions.get(item));
  }, [columnDefinitions]);

  return useDefaultColumns(visibleColumns, columnDefinitions);
};

export function useRepoColumnDefs() {
  const columnMapping = useMemo(() => {
    const mapping = {
      submitTime: {
        field: 'SubmitTime',
        title: 'Submit Time',
        type: 'date',
        params: { milliseconds: true },
        initialSort: '-',
        initialWidth: 150,
        initialSortIndex: 0,
        description: 'Timestamp of the repo deal submission',
      },
      productType: {
        field: 'SubProductType',
        title: 'Product Type',
        type: 'text',
        initialWidth: 120,
        description: 'Type of repo product',
        params: {
          getLabel: formatProductType,
        },
      },
      tradeDate: {
        field: 'DealInfo.TradeTime',
        title: getColumnTitle(repoBookingFieldDefinitions.tradeDate),
        type: 'date',
        description: getColumnTooltip(repoBookingFieldDefinitions.tradeDate),
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      effectiveDate: {
        field: 'DealInfo.EffectiveTime',
        title: getColumnTitle(repoBookingFieldDefinitions.effectiveDate),
        type: 'date',
        description: getColumnTooltip(repoBookingFieldDefinitions.effectiveDate),
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      status: {
        field: 'Status',
        type: 'text',
        initialWidth: 80,
        description: 'Status of the repo deal',
      },
      productTemplate: getProductTemplateBlotterColumn({
        description: 'Product Template Symbol used to generate the repo deal',
      }),
      symbol: {
        field: 'Symbol',
        title: 'Symbol',
        type: 'security',
        initialWidth: 200,
        description: 'Resolved repo symbol based on Product Template and Repurchase Date',
      },
      borrowedAsset: {
        field: 'Symbol',
        title: 'Borrowed Asset',
        type: 'currency',
        initialWidth: 100,
        description: 'Cash asset borrowed or lent for the repo',
        params: {
          showDescription: false,
          symbolField: 'Symbol',
          securityCurrencyField: 'ProductDetails.BorrowedAsset',
        },
      },
      repoAsset: {
        field: 'Symbol',
        title: 'Repo Asset',
        type: 'currency',
        initialWidth: 100,
        description: 'Collateral asset for the repo',
        params: {
          showDescription: false,
          symbolField: 'Symbol',
          securityCurrencyField: 'ProductDetails.RepoAsset',
        },
      },
      term: {
        field: 'Symbol',
        title: 'Term',
        type: 'securityText',
        initialWidth: 80,
        description: 'Repo term from the product template',
        params: {
          symbolField: 'Symbol',
          securityField: 'ProductDetails.Term',
        },
      },
      termType: {
        field: 'Symbol',
        title: 'Term Type',
        type: 'securityText',
        initialWidth: 90,
        description: 'Repo termination type',
        params: {
          symbolField: 'Symbol',
          securityField: 'ProductDetails.TermType',
          getLabel: (value: string | undefined) => {
            if (value === LoanQuoteTerminationTypeEnum.OpenTerm) {
              return 'Open';
            }
            if (value === LoanQuoteTerminationTypeEnum.Fixed) {
              return 'Fixed';
            }
            return '';
          },
        },
      },
      repurchaseDate: {
        field: 'DealInfo.ExpiryTime',
        type: 'date',
        title: getColumnTitle(repoBookingFieldDefinitions.expiryTime),
        description: getColumnTooltip(repoBookingFieldDefinitions.expiryTime),
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      settlementDate: {
        field: 'DealInfo.SettleTime',
        type: 'date',
        title: getColumnTitle(repoBookingFieldDefinitions.settleTime),
        description: getColumnTooltip(repoBookingFieldDefinitions.settleTime),
        params: {
          customFormatter: formatOtcDealBlotterLocalDateTime,
        },
        tooltipValueGetter: ({ value, data }) =>
          formatOtcDealBookingTimezoneTooltip(value, data?.DealInfo.SettleCutoffTimezone ?? 'UTC'),
      },
      counterparty: {
        field: 'Counterparty',
        type: 'counterparty',
        title: getColumnTitle(repoBookingFieldDefinitions.counterparty),
        description: getColumnTooltip(repoBookingFieldDefinitions.counterparty),
      },
      marketAccount: {
        field: 'MarketAccount',
        title: getColumnTitle(repoBookingFieldDefinitions.marketAccount),
        type: 'marketAccount',
        description: getColumnTooltip(repoBookingFieldDefinitions.marketAccount),
      },
      direction: {
        field: 'DealInfo.Direction',
        title: getColumnTitle(repoBookingFieldDefinitions.direction),
        type: 'prettyText',
        description: getColumnTooltip(repoBookingFieldDefinitions.direction),
      },
      // @ts-expect-error - TODO: (UI-7132) Fix this with a better generic Security type for securityFieldProperty
      repoAmount: {
        field: 'DealInfo.RepoAmount',
        title: getColumnTitle(repoBookingFieldDefinitions.repoAmount),
        type: 'size',
        description: getColumnTooltip(repoBookingFieldDefinitions.repoAmount),
        params: {
          securityField: 'Symbol',
          securityFieldProperty: 'ProductDetails.RepoAsset',
        },
      },
      rate: {
        field: 'DealInfo.Rate',
        title: getColumnTitle(repoBookingFieldDefinitions.rate),
        type: 'percent',
        initialWidth: 100,
        description: getColumnTooltip(repoBookingFieldDefinitions.rate),
      },
      primeBroker: {
        field: 'DealInfo.PrimeBroker',
        title: getColumnTitle(repoBookingFieldDefinitions.primeBroker),
        type: 'text',
        description: getColumnTooltip(repoBookingFieldDefinitions.primeBroker),
      },
      haircutPct: {
        field: 'DealInfo.HaircutPct',
        title: getColumnTitle(repoBookingFieldDefinitions.haircutPct),
        type: 'percent',
        description: getColumnTooltip(repoBookingFieldDefinitions.haircutPct),
      },
      purchasePrice: {
        field: 'DealInfo.PurchasePrice',
        title: getColumnTitle(repoBookingFieldDefinitions.purchasePrice),
        type: 'price',
        description: getColumnTooltip(repoBookingFieldDefinitions.purchasePrice),
        params: {
          assetField: 'Symbol',
          quoteCurrencySecurityField: 'ProductDetails.BorrowedAsset',
          useQuoteCurrencyIncrement: true,
        },
      },
      repoInterest: {
        field: 'DealInfo.RepoInterest',
        title: getColumnTitle(repoBookingFieldDefinitions.repoInterest),
        type: 'price',
        description: getColumnTooltip(repoBookingFieldDefinitions.repoInterest),
        params: {
          assetField: 'Symbol',
          quoteCurrencySecurityField: 'ProductDetails.BorrowedAsset',
          useQuoteCurrencyIncrement: true,
        },
      },
      repurchasePrice: {
        field: 'DealInfo.RepurchasePrice',
        title: getColumnTitle(repoBookingFieldDefinitions.repurchasePrice),
        type: 'price',
        description: getColumnTooltip(repoBookingFieldDefinitions.repurchasePrice),
        params: {
          assetField: 'Symbol',
          quoteCurrencySecurityField: 'ProductDetails.BorrowedAsset',
          useQuoteCurrencyIncrement: true,
        },
      },
      subAccount: {
        field: 'SubAccount',
        type: 'subAccount',
        title: 'Sub Account(s)',
        description: 'The internal account used to segregate trades and deals',
      },
      user: otcDealUserColumn,
      dealId: {
        field: 'ID',
        type: 'id',
        initialWidth: 100,
        title: 'Deal ID',
        description: 'The unique Talos identifier for the repo deal',
      },
      businessDayConvention: {
        field: 'DealInfo.BusinessDayHolidayConvention',
        title: 'Business Day Convention',
        type: 'text',
        description: 'Business day convention used for repo settlement dates',
      },
      holidayCalendars: {
        field: 'DealInfo.HolidayCalendars',
        title: 'Holiday Calendars',
        type: 'text',
        description: 'Holiday calendars used for repo settlement dates',
        params: {
          getLabel: formatHolidayCalendars,
        },
      },
      settlementCycle: {
        field: 'DealInfo.SettleCycle',
        title: 'Settlement Cycle',
        type: 'text',
        description: 'Settlement cycle for the repo',
        params: {
          getLabel: formatSettlementCycle,
        },
      },
      timestamp: {
        field: 'Timestamp',
        title: 'Updated At',
        type: 'date',
        params: { milliseconds: true },
      },
    } as const satisfies Record<string, ColumnDef<OTCDealData>>;

    return mapping;
  }, []);

  return columnMapping;
}
